import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';
import ReportBlockMenu from '../components/social/ReportBlockMenu';
import ThumbnailCropModal from '../components/common/ThumbnailCropModal';
import VideoMedia from '../components/common/VideoMedia';

// ─── Display helpers ──────────────────────────────────────────────────────────

const DISPLAY = {
  manual: 'Manual', automatic: 'Automatic', cvt: 'CVT', dct: 'DCT',
  fwd: 'FWD', rwd: 'RWD', awd: 'AWD', '4wd': '4WD',
  petrol: 'Petrol', diesel: 'Diesel', electric: 'Electric', hybrid: 'Hybrid', other: 'Other',
};
const fmt = (v) => (v ? (DISPLAY[v.toLowerCase()] || v) : null);

const fmtModDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isVideoItem = (img) => img?.mediaType === 'video';

// Generate a JPEG poster blob from the first frame of a video File. Resolves
// with { blob, w, h } or rejects on load/decode failure. Used when uploading
// gallery videos so the server gets a poster image alongside the clip.
const posterFromVideoFile = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = url;
  const cleanup = () => { URL.revokeObjectURL(url); };
  const fail = (err) => { cleanup(); reject(err); };
  video.onloadedmetadata = () => {
    if (video.duration > 60) { cleanup(); reject(new Error('TOO_LONG')); return; }
    // Seek to a frame near the start to avoid a black first frame.
    const t = Math.min(0.1, video.duration / 2 || 0);
    const onSeeked = () => {
      try {
        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
          cleanup();
          if (blob) resolve({ blob, w, h });
          else reject(new Error('NO_BLOB'));
        }, 'image/jpeg', 0.85);
      } catch (err) { fail(err); }
    };
    video.onseeked = onSeeked;
    try { video.currentTime = t; } catch { onSeeked(); }
  };
  video.onerror = () => fail(new Error('LOAD_ERROR'));
});

// ─── Star rating ──────────────────────────────────────────────────────────────

const StarRating = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} onClick={() => onChange && onChange(n)}
        style={{ fontSize: '1.5rem', cursor: onChange ? 'pointer' : 'default', color: n <= value ? '#c9ced6' : '#2a3142' }}>★</span>
    ))}
  </div>
);

// ─── Mods timeline constants ──────────────────────────────────────────────────

const T_NODE_W = 175;
const T_NODE_H = 155;
const T_NODE_GAP = 12;
const T_CON_W = 60;

const STATUS_COLORS = {
  installed: '#4caf7d',
  planned: '#5b7fff',
  in_progress: '#f5a623',
  removed: '#e84545',
};

// ─── Mod icon ─────────────────────────────────────────────────────────────────

const ModIcon = ({ category, name }) => {
  const c = ((category || '') + ' ' + (name || '')).toLowerCase();
  const p = { width: 40, height: 40, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.3, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (c.match(/turbo|supercharg|boost/)) return <svg {...p}><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>;
  if (c.match(/exhaust|muffler|pipe/)) return <svg {...p}><path d="M3 12h10M13 8h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4V8z"/><circle cx="20" cy="12" r="1"/></svg>;
  if (c.match(/intercooler|radiator|cooling/)) return <svg {...p}><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="7" y1="6" x2="7" y2="18"/><line x1="12" y1="6" x2="12" y2="18"/><line x1="17" y1="6" x2="17" y2="18"/></svg>;
  if (c.match(/fuel|inject|pump/)) return <svg {...p}><path d="M3 22V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16"/><path d="M3 11h12"/><path d="M17 11h1a2 2 0 0 1 2 2v3a1 1 0 0 0 2 0v-6l-2-3"/></svg>;
  if (c.match(/suspend|spring|shock|coilover/)) return <svg {...p}><line x1="12" y1="2" x2="12" y2="6"/><path d="M8 6h8M10 10h4M8 14h8M10 18h4"/><line x1="12" y1="18" x2="12" y2="22"/></svg>;
  if (c.match(/brake|rotor|caliper/)) return <svg {...p}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path d="M12 3v5M12 16v5M3 12h5M16 12h5"/></svg>;
  if (c.match(/wheel|tire|rim/)) return <svg {...p}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="3" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="21"/><line x1="3" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="21" y2="12"/></svg>;
  if (c.match(/exterior|body|paint|wrap|aero|spoiler|lip/)) return <svg {...p}><path d="M2 17l5-5h10l5 5"/><path d="M3 17h18v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2z"/><path d="M7 12V9l2-4h6l2 4v3"/></svg>;
  if (c.match(/interior|seat|steer|dash|trim/)) return <svg {...p}><path d="M20 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>;
  if (c.match(/ecu|tune|electronic|chip|module/)) return <svg {...p}><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="2" x2="9" y2="4"/><line x1="15" y1="2" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="22"/><line x1="15" y1="20" x2="15" y2="22"/></svg>;
  if (c.match(/audio|speaker|sound|subwoofer/)) return <svg {...p}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>;
  if (c.match(/transmiss|gear|clutch|diff/)) return <svg {...p}><circle cx="12" cy="12" r="3"/><circle cx="5" cy="5" r="2"/><circle cx="19" cy="5" r="2"/><circle cx="19" cy="19" r="2"/><line x1="12" y1="9" x2="5" y2="7"/><line x1="12" y1="9" x2="19" y2="7"/><line x1="12" y1="15" x2="19" y2="17"/></svg>;
  if (c.match(/engine|motor|block|head|cam/)) return <svg {...p}><rect x="2" y="8" width="20" height="12" rx="2"/><path d="M6 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"/><line x1="6" y1="13" x2="6" y2="15"/><line x1="10" y1="13" x2="10" y2="15"/><line x1="14" y1="13" x2="14" y2="15"/><line x1="18" y1="13" x2="18" y2="15"/></svg>;
  return <svg {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
};

// ─── Mod node card ────────────────────────────────────────────────────────────

const ModNode = ({ mod, isOwner, onEdit, onDelete }) => {
  const [hov, setHov] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative',
        width: T_NODE_W,
        minHeight: T_NODE_H,
        background: hov ? '#242426' : '#1c1c1e',
        border: `1px solid ${hov ? '#48484a' : '#2c2c2e'}`,
        borderRadius: 16,
        padding: '18px 16px',
        boxSizing: 'border-box',
        flexShrink: 0,
        transition: 'background 0.15s, border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      {/* Edit — bare pencil icon, no box */}
      {isOwner && hov && (
        <button onClick={e => { e.stopPropagation(); onEdit(); }}
          style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 2, display: 'flex', zIndex: 2 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
      )}

      {/* Icon */}
      <div style={{ color: '#7e7e8a' }}>
        <ModIcon category={mod.category} name={mod.customPartName} />
      </div>

      {/* Name */}
      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center' }}>
        {mod.customPartName}
      </div>

      {/* Description */}
      {mod.customPartDescription && (
        <div style={{ fontSize: '0.72rem', color: '#8e8e93', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center' }}>
          {mod.customPartDescription}
        </div>
      )}

      {/* Price — only when set */}
      {mod.installCost && (
        <div style={{ fontSize: '0.8rem', color: '#4caf7d', fontWeight: 700 }}>
          ${Number(mod.installCost).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// ─── SVG connector between timeline columns ───────────────────────────────────

const ModsConnector = ({ fromCount, toCount }) => {
  const fromH = fromCount * T_NODE_H + Math.max(0, fromCount - 1) * T_NODE_GAP;
  const toH = toCount * T_NODE_H + Math.max(0, toCount - 1) * T_NODE_GAP;
  const svgH = Math.max(fromH, toH);
  const W = T_CON_W;
  const stroke = 'rgba(255,255,255,0.55)';
  const sw = 1.8;
  const GAP = 10;
  const ARW = 6;

  const fromY = svgH / 2;
  const toOff = (svgH - toH) / 2;
  const toYs = Array.from({ length: toCount }, (_, i) =>
    toOff + i * (T_NODE_H + T_NODE_GAP) + T_NODE_H / 2
  );

  // Open chevron arrowhead at (x, y)
  const chevron = (x, y) =>
    `M ${x - ARW},${y - ARW} L ${x},${y} L ${x - ARW},${y + ARW}`;

  if (toCount === 1) {
    const ty = toYs[0];
    return (
      <svg width={W} height={svgH} style={{ flexShrink: 0, display: 'block' }}>
        <line x1={GAP} y1={fromY} x2={W - GAP - ARW} y2={ty} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <path d={chevron(W - GAP, ty)} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  const spineX = Math.floor(W * 0.45);
  const spineTop = Math.min(...toYs);
  const spineBot = Math.max(...toYs);

  return (
    <svg width={W} height={svgH} style={{ flexShrink: 0, display: 'block' }}>
      {/* Stem from left node to spine */}
      <line x1={GAP} y1={fromY} x2={spineX} y2={fromY} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      {/* Vertical bracket spine */}
      <line x1={spineX} y1={spineTop} x2={spineX} y2={spineBot} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      {/* Branch to each target with chevron */}
      {toYs.map((y, i) => (
        <g key={i}>
          <line x1={spineX} y1={y} x2={W - GAP - ARW} y2={y} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
          <path d={chevron(W - GAP, y)} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ))}
    </svg>
  );
};

// ─── Wavy dots background ─────────────────────────────────────────────────────

const WAVY_DOTS_SVG = (() => {
  const VW = 1600, VH = 560;
  const COLS = 260, ROWS = 60;
  let s = '';
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const baseX = (c / (COLS - 1)) * VW;
      const baseY = (r / (ROWS - 1)) * VH;
      const dy =
        Math.sin(c * 0.11 + r * 0.5) * 10 +
        Math.sin(c * 0.21 - r * 0.85) * 6 +
        Math.sin(c * 0.06 + r * 1.0) * 3.5;
      const cy = baseY + dy;
      const t = (Math.sin(c * 0.1 + r * 0.44) + 1) / 2;
      const opacity = (0.04 + t * 0.2).toFixed(2);
      const radius = (0.55 + t * 0.85).toFixed(1);
      s += `<circle cx="${Math.round(baseX)}" cy="${cy.toFixed(1)}" r="${radius}" fill="rgba(255,255,255,${opacity})"/>`;
    }
  }
  return s;
})();

const WavyDotsBg = () => (
  <svg
    aria-hidden="true"
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    viewBox="0 0 1600 560"
    preserveAspectRatio="xMidYMid slice"
    dangerouslySetInnerHTML={{ __html: WAVY_DOTS_SVG }}
  />
);

// ─── Full mods timeline ───────────────────────────────────────────────────────

const ModsTimeline = ({ mods, isOwner, onAdd, onEdit, onDelete }) => {
  const sorted = [...mods].sort((a, b) =>
    new Date(a.installDate || a.createdAt) - new Date(b.installDate || b.createdAt)
  );

  // Group by installDate only when explicitly set; otherwise each mod is its own column.
  // This prevents same-day createdAt timestamps from collapsing all mods into one column.
  const groups = [];
  sorted.forEach(mod => {
    if (mod.installDate) {
      const key = mod.installDate.split('T')[0];
      const g = groups.find(x => x.key === key);
      if (g) g.mods.push(mod);
      else groups.push({ key, mods: [mod] });
    } else {
      groups.push({ key: `id-${mod.id}`, mods: [mod] });
    }
  });

  if (mods.length === 0) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center' }}>
        <div style={{ width: 52, height: 52, background: '#1c1c1e', border: '1px solid #2c2c2e', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#8e8e93' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
        </div>
        <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '0.95rem', marginBottom: 6 }}>No mods yet</div>
        <div style={{ color: '#8e8e93', fontSize: '0.82rem', marginBottom: 20 }}>Track every upgrade on this build</div>
        {isOwner && <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '8px 20px', background: '#ffffff', color: '#000000' }} onClick={onAdd}>+ Add first mod</button>}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, paddingTop: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: '#ffffff', letterSpacing: '-0.02em' }}>Mods Timeline</div>
          <div style={{ fontSize: '0.78rem', color: '#8e8e93', marginTop: 3 }}>Chronological view of your build · {mods.length} mod{mods.length !== 1 ? 's' : ''}</div>
        </div>
        {isOwner && (
          <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '7px 14px', fontWeight: 700, background: '#ffffff', color: '#000000' }} onClick={onAdd}>+ Add mod</button>
        )}
      </div>

      {/* Outer box: visual border + fixed dot background */}
      <div style={{
        position: 'relative',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 18,
        backgroundColor: '#060606',
        overflow: 'hidden',
      }}>
        <WavyDotsBg />
        {/* Inner scroll container sits above the fixed background */}
        <div style={{ position: 'relative', zIndex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '52px 36px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', minWidth: 'max-content' }}>
          {groups.map((group, i) => (
            <React.Fragment key={group.key}>
              {i > 0 && <ModsConnector fromCount={groups[i - 1].mods.length} toCount={group.mods.length} />}
              <div style={{ display: 'flex', flexDirection: 'column', gap: T_NODE_GAP, flexShrink: 0 }}>
                {group.mods.map(mod => (
                  <ModNode
                    key={mod.id}
                    mod={mod}
                    isOwner={isOwner}
                    onEdit={() => onEdit(mod)}
                    onDelete={() => onDelete(mod.id)}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
        </div> {/* end inner scroll */}
      </div>   {/* end outer box */}
    </div>
  );
};

// ─── Add / Edit mod modal ─────────────────────────────────────────────────────

const MOD_CATEGORIES = [
  ['exhaust', 'Exhaust'],
  ['turbo', 'Turbo / Supercharger'],
  ['intercooler', 'Intercooler / Cooling'],
  ['engine', 'Engine'],
  ['fuel', 'Fuel System'],
  ['suspension', 'Suspension'],
  ['brakes', 'Brakes'],
  ['wheels', 'Wheels / Tires'],
  ['exterior', 'Exterior / Aero'],
  ['interior', 'Interior'],
  ['electronics', 'Electronics / ECU'],
  ['transmission', 'Transmission / Drivetrain'],
  ['audio', 'Audio'],
  ['other', 'Other'],
];

const AddModModal = ({ mod, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState({
    customPartName: mod?.customPartName || '',
    category: mod?.category || '',
    customPartDescription: mod?.customPartDescription || '',
    installCost: mod?.installCost || '',
    installDate: mod?.installDate ? mod.installDate.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customPartName.trim()) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        installCost: form.installCost ? parseFloat(form.installCost) : null,
        installDate: form.installDate || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="event-modal-overlay" onClick={onClose}>
      <div className="event-modal" style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="event-modal-header">
          <h2>{mod ? 'Edit Mod' : 'Add Mod'}</h2>
          <button className="event-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="event-modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Mod Name *</label>
              <input className="input" required value={form.customPartName} onChange={e => set('customPartName', e.target.value)} placeholder="e.g. Turbo Kit, Exhaust System" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select className="input" value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">— select —</option>
                {MOD_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Install Date</label>
              <input className="input" type="date" value={form.installDate} onChange={e => set('installDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Cost ($)</label>
              <input className="input" type="number" min="0" step="0.01" value={form.installCost} onChange={e => set('installCost', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Description / Brand</label>
              <input className="input" value={form.customPartDescription} onChange={e => set('customPartDescription', e.target.value)} placeholder="e.g. Precision 6870 Twin Turbo" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4, alignItems: 'center' }}>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (mod ? 'Save Changes' : 'Add Mod')}</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            {mod && onDelete && (
              <button type="button" onClick={onDelete}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e84545', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, padding: '4px 0' }}>
                Delete mod
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const VehicleDetail = () => {
  const { id } = useParams();
  const { user } = useSelector(s => s.auth);
  const navigate = useNavigate();
  const [uv, setUv] = useState(null);
  const [liked, setLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('specs');
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', body: '', pros: '', cons: '', ownershipMonths: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [mods, setMods] = useState([]);
  const [images, setImages] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [galleryEditMode, setGalleryEditMode] = useState(false);
  const [selectedImgIds, setSelectedImgIds] = useState(new Set());
  const [reorderMode, setReorderMode] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [coverModalImg, setCoverModalImg] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showModModal, setShowModModal] = useState(false);
  const [editingMod, setEditingMod] = useState(null);
  const audioRef = useRef();

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setLightboxIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setLightboxIdx(i => Math.min(images.length - 1, i + 1));
      if (e.key === 'Escape') setLightboxIdx(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, images.length]);

  useEffect(() => {
    const load = async () => {
      try {
        const [uvRes, reviewsRes, modsRes] = await Promise.all([
          api.get(`/user-vehicles/${id}`),
          api.get(`/vehicles/${id}/reviews`).catch(() => ({ data: { reviews: [] } })),
          api.get(`/user-vehicles/${id}/mods`),
        ]);
        setUv(uvRes.data.userVehicle);
        setLiked(uvRes.data.liked);
        setImages(uvRes.data.userVehicle.images || []);
        setReviews(reviewsRes.data.reviews);
        setMods(modsRes.data.mods);
      } catch { toast.error('Vehicle not found'); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const toggleLike = async () => {
    try {
      if (liked) {
        await api.delete(`/user-vehicles/${id}/like`);
        setLiked(false);
        setUv(v => ({ ...v, likeCount: v.likeCount - 1 }));
      } else {
        await api.post(`/user-vehicles/${id}/like`);
        setLiked(true);
        setUv(v => ({ ...v, likeCount: v.likeCount + 1 }));
      }
    } catch { toast.error('Failed'); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/vehicles/${uv.vehicle.id}/reviews`, reviewForm);
      setReviews(r => [data.review, ...r]);
      setShowReviewForm(false);
      toast.success('Review posted!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to post review'); }
  };

  const openEdit = () => {
    const v2 = uv.vehicle;
    setEditForm({
      nickname: uv.nickname || '', color: uv.color || v2.color || '',
      odometer: uv.odometer || '', condition: uv.condition || '',
      notes: uv.notes || '', transmission: v2.transmission || '',
      driveType: v2.driveType || '', fuelType: v2.fuelType || '',
      horsepower: v2.horsepower || '', torque: v2.torque || '',
      topSpeed: v2.topSpeed || '', engineDisplacement: v2.engineDisplacement || '',
      engineCylinders: v2.engineCylinders || '', zeroToHundred: v2.zeroToHundred || '',
      description: v2.description || '',
    });
    setShowEdit(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      const { data } = await api.put(`/user-vehicles/${id}`, {
        nickname: editForm.nickname || null, color: editForm.color || null,
        odometer: editForm.odometer ? parseInt(editForm.odometer) : null,
        condition: editForm.condition || null, notes: editForm.notes || null,
        transmission: editForm.transmission || null,
        driveType: editForm.driveType || null, fuelType: editForm.fuelType || null,
        horsepower: editForm.horsepower ? parseInt(editForm.horsepower) : null,
        torque: editForm.torque ? parseInt(editForm.torque) : null,
        topSpeed: editForm.topSpeed ? parseInt(editForm.topSpeed) : null,
        engineDisplacement: editForm.engineDisplacement ? parseFloat(editForm.engineDisplacement) : null,
        engineCylinders: editForm.engineCylinders ? parseInt(editForm.engineCylinders) : null,
        zeroToHundred: editForm.zeroToHundred ? parseFloat(editForm.zeroToHundred) : null,
        description: editForm.description || null,
      });
      setUv(prev => ({ ...prev, ...data.userVehicle, vehicle: { ...prev.vehicle, ...data.userVehicle.vehicle } }));
      toast.success('Vehicle updated!');
      setShowEdit(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update'); }
    finally { setEditSubmitting(false); }
  };

  const setEF = (k, v2) => setEditForm(f => ({ ...f, [k]: v2 }));

  const handleSaveMod = async (formData) => {
    try {
      if (editingMod) {
        const { data } = await api.put(`/user-vehicles/${id}/mods/${editingMod.id}`, formData);
        setMods(ms => ms.map(m => m.id === editingMod.id ? data.mod : m));
        toast.success('Mod updated!');
      } else {
        const { data } = await api.post(`/user-vehicles/${id}/mods`, formData);
        setMods(ms => [...ms, data.mod]);
        toast.success('Mod added!');
      }
      setShowModModal(false);
      setEditingMod(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save mod'); }
  };

  const handleDeleteMod = async (modId) => {
    try {
      await api.delete(`/user-vehicles/${id}/mods/${modId}`);
      setMods(ms => ms.filter(m => m.id !== modId));
      toast.success('Mod removed');
    } catch { toast.error('Failed to remove mod'); }
  };

  // ── Gallery reorder (drag & drop) ──────────────────────────────────────────
  const persistOrder = async (ordered) => {
    try {
      await api.put(`/user-vehicles/${id}/images/reorder`, { orderedIds: ordered.map(im => im.id) });
    } catch { toast.error('Failed to save order'); }
  };

  const handleDrop = (toIdx) => {
    setOverIdx(null);
    const from = dragIdx;
    setDragIdx(null);
    if (from === null || from === toIdx) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    setImages(next);
    persistOrder(next);
  };

  // ── Cover photo set (from crop modal) ──────────────────────────────────────
  const handleCoverSet = (primaryImageUrl, imageId) => {
    setUv(prev => ({ ...prev, primaryImageUrl }));
    setImages(prev => prev.map(im => ({ ...im, isPrimary: im.id === imageId })));
    setCoverModalImg(null);
  };

  if (loading) return <div className="loading-page"><div className="loading-spinner" /></div>;
  if (!uv) return <div className="container page"><div className="empty-state"><h3>Vehicle not found</h3></div></div>;

  const v = uv.vehicle;
  const isOwner = user?.id === uv.userId;
  // Cover is decoupled from gallery order: prefer the stored cover (which may be
  // a baked crop), then the flagged image, then the first photo.
  const primaryImg = uv.primaryImageUrl || images.find(i => i.isPrimary)?.imageUrl || images[0]?.imageUrl;

  return (
    <>
      <div className="container page">

        {/* ── Thin vehicle header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, position: 'relative' }}>
          {/* Back — uses absolute CSS from .vehicle-back-btn (left: -48px) */}
          <button className="vehicle-back-btn" onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', borderRadius: 0, padding: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* Thumbnail */}
          <div style={{ width: 128, height: 76, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: '#1c1c1e', border: '1px solid #2c2c2e' }}>
            {primaryImg
              ? <img src={primaryImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#636366', fontWeight: 700 }}>NO PHOTO</div>}
          </div>

          {/* Title block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {uv.nickname || `${v.year} ${v.make} ${v.model}`}
              </h1>
              {uv.isDreamBuild && <span className="badge badge-purple" style={{ flexShrink: 0 }}>Dream Build</span>}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#636366', marginTop: 2 }}>
              {[uv.likeCount > 0 && `${uv.likeCount} likes`, uv.viewCount > 0 && `${uv.viewCount} views`].filter(Boolean).join(' · ')}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            <button onClick={toggleLike} className={liked ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}>
              {liked ? '♥ Liked' : '♡ Like'}
            </button>
            {isOwner && <button className="btn-secondary btn-sm" onClick={openEdit}>Edit vehicle</button>}
            {!isOwner && uv.userId && (
              <ReportBlockMenu
                targetType="vehicle"
                targetId={uv.id}
                targetUserId={uv.userId}
                targetLabel="vehicle"
                onReported={() => navigate(-1)}
                onBlocked={() => navigate(-1)}
              />
            )}
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="tabs">
          <button className={`tab ${tab === 'specs' ? 'active' : ''}`} onClick={() => setTab('specs')}>Specs</button>
          <button className={`tab ${tab === 'mods' ? 'active' : ''}`} onClick={() => setTab('mods')}>Mods ({mods.length})</button>
          <button className={`tab ${tab === 'gallery' ? 'active' : ''}`} onClick={() => setTab('gallery')}>Gallery ({images.length})</button>
          <button className={`tab ${tab === 'reviews' ? 'active' : ''}`} onClick={() => setTab('reviews')}>Comments ({reviews.length})</button>
        </div>

        {/* ── Specs tab ── */}
        {tab === 'specs' && (
          <>
            {/* Owner + meta row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              <Link to={`/profile/${uv.owner?.username}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                {uv.owner?.profileImageUrl
                  ? <img src={uv.owner.profileImageUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                  : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2c2c2e', border: '1px solid #3a3a3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#ffffff', fontSize: '0.8rem' }}>{uv.owner?.username?.[0]?.toUpperCase()}</div>}
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ffffff' }}>@{uv.owner?.username}</span>
              </Link>
              {uv.marketValue && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: '#1c1c1e', border: '1px solid #2c2c2e', borderRadius: 8 }}>
                  <span style={{ fontSize: '0.7rem', color: '#636366', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#ffffff' }}>${Number(uv.marketValue).toLocaleString()}</span>
                </div>
              )}
            </div>
            {v.description && (
              <p style={{ color: '#8e8e93', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: 20, marginTop: 0 }}>{v.description}</p>
            )}
            <table className="specs-table">
                  <tbody>
                    {[
                      ['Make', v.make], ['Model', v.model], ['Year', v.year], ['Type', v.vehicleType],
                      ['Category', v.category], ['Color', uv.color || v.color],
                      ['Horsepower', v.horsepower ? `${v.horsepower} hp` : null],
                      ['Torque', v.torque ? `${v.torque} Nm` : null],
                      ['Engine', v.engineCylinders ? `${v.engineCylinders}-cyl ${v.engineDisplacement}L` : v.engineDisplacement ? `${v.engineDisplacement}L` : null],
                      ['Transmission', fmt(v.transmission)], ['Drive Type', fmt(v.driveType)], ['Fuel Type', fmt(v.fuelType)],
                      ['Top Speed', v.topSpeed ? `${v.topSpeed} km/h` : null],
                      ['0–100 km/h', v.zeroToHundred ? `${v.zeroToHundred}s` : null],
                      ['¼ Mile', v.quarterMileTime ? `${v.quarterMileTime}s @ ${v.quarterMileSpeed || '—'} km/h` : null],
                      ['Odometer', uv.odometer ? `${uv.odometer.toLocaleString()} km` : null],
                      ['Condition', uv.condition], ['Acquired', uv.acquisitionDate],
                      ['Market Value', uv.marketValue ? `$${Number(uv.marketValue).toLocaleString()}` : null],
                    ].filter(([, val]) => val).map(([label, val], i) => (
                      <tr key={i}>
                        <td>{label}</td>
                        <td>
                          {val}
                          {(label === '0–100 km/h' && v.zeroToHundredCertified) && <span className="certified-badge">Certified {v.certificationSource}</span>}
                          {(label === '¼ Mile' && v.quarterMileCertified) && <span className="certified-badge">Certified {v.certificationSource}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── Mods tab ── */}
            {tab === 'mods' && (
              <ModsTimeline
                mods={mods}
                isOwner={isOwner}
                onAdd={() => { setEditingMod(null); setShowModModal(true); }}
                onEdit={(mod) => { setEditingMod(mod); setShowModModal(true); }}
                onDelete={handleDeleteMod}
              />
            )}

            {/* ── Gallery tab ── */}
            {tab === 'gallery' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
                  {isOwner && (
                    <label className="btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      + Add Media
                      <input type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        e.target.value = '';
                        if (files.length === 0) return;
                        const formData = new FormData();
                        let hasMedia = false;
                        for (const f of files) {
                          if (f.type.startsWith('video/')) {
                            let poster;
                            try {
                              poster = await posterFromVideoFile(f);
                            } catch (err) {
                              if (err && err.message === 'TOO_LONG') {
                                toast.error(`"${f.name}" is longer than 60s and was skipped`);
                              } else {
                                toast.error(`Could not process "${f.name}"`);
                              }
                              continue;
                            }
                            // Keep 'videos' and 'posters' in the same order.
                            formData.append('videos', f);
                            formData.append('posters', new File([poster.blob], 'poster.jpg', { type: 'image/jpeg' }));
                            hasMedia = true;
                          } else if (f.type.startsWith('image/')) {
                            formData.append('images', f);
                            hasMedia = true;
                          }
                        }
                        if (!hasMedia) return;
                        try {
                          const { data } = await api.post(`/user-vehicles/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          setImages(imgs => [...imgs, ...data.images]);
                          toast.success('Media added!');
                        } catch { toast.error('Failed to upload'); }
                      }} />
                    </label>
                  )}
                  {isOwner && images.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {galleryEditMode && selectedImgIds.size > 0 && (
                        <button className="btn-sm" style={{ background: 'rgba(220,50,50,0.12)', border: '1px solid rgba(220,50,50,0.3)', color: '#e84545', cursor: 'pointer', padding: '4px 12px', borderRadius: 8, fontSize: '0.8rem' }}
                          onClick={async () => {
                            if (!window.confirm(`Remove ${selectedImgIds.size} photo${selectedImgIds.size > 1 ? 's' : ''}?`)) return;
                            try {
                              await Promise.all([...selectedImgIds].map(imgId => api.delete(`/user-vehicles/${id}/images/${imgId}`)));
                              setImages(imgs => imgs.filter(im => !selectedImgIds.has(im.id)));
                              setSelectedImgIds(new Set());
                              setGalleryEditMode(false);
                              toast.success('Photos removed');
                            } catch { toast.error('Failed to remove photos'); }
                          }}>
                          Delete ({selectedImgIds.size})
                        </button>
                      )}
                      {images.length > 1 && (
                        <button className="btn-secondary btn-sm" onClick={() => { setReorderMode(m => !m); setGalleryEditMode(false); setSelectedImgIds(new Set()); }}>
                          {reorderMode ? 'Done' : 'Reorder'}
                        </button>
                      )}
                      <button className="btn-secondary btn-sm" onClick={() => { setGalleryEditMode(m => !m); setReorderMode(false); setSelectedImgIds(new Set()); }}>
                        {galleryEditMode ? 'Done' : 'Edit'}
                      </button>
                    </div>
                  )}
                </div>
                {(reorderMode || galleryEditMode) && (
                  <div style={{ fontSize: '0.8rem', color: '#8e8e93', marginBottom: 12 }}>
                    {reorderMode
                      ? 'Drag photos to change their order.'
                      : 'Tap photos to select for removal, or use “Set as thumbnail” to choose the card image.'}
                  </div>
                )}
                {images.length === 0
                  ? <div style={{ color: '#8e8e93', padding: '40px 0', textAlign: 'center' }}>No photos yet.</div>
                  : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                      {images.map((img, i) => {
                        const selected = selectedImgIds.has(img.id);
                        const isCover = !!img.isPrimary;
                        const isVideo = isVideoItem(img);
                        return (
                          <div key={img.id}
                            draggable={reorderMode}
                            onDragStart={reorderMode ? () => setDragIdx(i) : undefined}
                            onDragOver={reorderMode ? (e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); } : undefined}
                            onDrop={reorderMode ? (e) => { e.preventDefault(); handleDrop(i); } : undefined}
                            onDragEnd={reorderMode ? () => { setDragIdx(null); setOverIdx(null); } : undefined}
                            style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', borderRadius: 8,
                              cursor: reorderMode ? 'grab' : 'pointer',
                              opacity: reorderMode && dragIdx === i ? 0.4 : 1,
                              outline: selected ? '3px solid #5b7fff' : (reorderMode && overIdx === i && dragIdx !== i ? '3px dashed rgba(255,255,255,0.6)' : 'none'),
                              outlineOffset: '-3px' }}
                            onClick={() => {
                              if (reorderMode) return;
                              if (galleryEditMode) {
                                setSelectedImgIds(prev => { const next = new Set(prev); next.has(img.id) ? next.delete(img.id) : next.add(img.id); return next; });
                              } else {
                                setLightboxIdx(i);
                              }
                            }}>
                            <img src={img.thumbnailUrl || img.imageUrl} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                              transition: 'transform 0.2s, opacity 0.2s', opacity: galleryEditMode && !selected ? 0.5 : 1, pointerEvents: 'none' }} />
                            {isVideo && !reorderMode && (
                              <span className="video-thumb-badge">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                              </span>
                            )}
                            {isCover && !reorderMode && (
                              <div style={{ position: 'absolute', top: 8, left: 8, padding: '2px 8px', borderRadius: 6, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', color: '#ffffff', textTransform: 'uppercase' }}>
                                Thumbnail
                              </div>
                            )}
                            {galleryEditMode && !isVideo && (
                              <button className="btn-sm"
                                onClick={(e) => { e.stopPropagation(); setCoverModalImg(img); }}
                                style={{ position: 'absolute', bottom: 8, left: 8, right: 8, background: '#f4f5f7', border: 'none', color: '#0a0a0a', cursor: 'pointer', padding: '4px 8px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700 }}>
                                {isCover ? 'Edit thumbnail' : 'Set as thumbnail'}
                              </button>
                            )}
                            {galleryEditMode && selected && (
                              <div style={{ position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: '50%', background: '#5b7fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            )}

            {/* ── Comments/Reviews tab ── */}
            {tab === 'reviews' && (
              <div>
                {user && <button className="btn-primary btn-sm" style={{ marginBottom: 20 }} onClick={() => setShowReviewForm(v => !v)}>Write a Review</button>}
                {showReviewForm && (
                  <div style={{ background: '#1c1c1e', border: '1px solid #2c2c2e', borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <form onSubmit={submitReview}>
                      <div className="form-group">
                        <label>Rating</label>
                        <StarRating value={reviewForm.rating} onChange={r => setReviewForm(f => ({ ...f, rating: r }))} />
                      </div>
                      <div className="form-group"><label>Title</label><input className="input" required value={reviewForm.title} onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))} /></div>
                      <div className="form-group"><label>Review</label><textarea className="input" required rows={4} value={reviewForm.body} onChange={e => setReviewForm(f => ({ ...f, body: e.target.value }))} /></div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group"><label>Pros</label><textarea className="input" rows={2} value={reviewForm.pros} onChange={e => setReviewForm(f => ({ ...f, pros: e.target.value }))} /></div>
                        <div className="form-group"><label>Cons</label><textarea className="input" rows={2} value={reviewForm.cons} onChange={e => setReviewForm(f => ({ ...f, cons: e.target.value }))} /></div>
                      </div>
                      <button type="submit" className="btn-primary btn-sm">Submit Review</button>
                    </form>
                  </div>
                )}
                {reviews.map(r => {
                  const rAuthorId = r.author?.id || r.userId;
                  const isOwnReview = user && rAuthorId === user.id;
                  return (
                    <div key={r.id} style={{ background: '#1c1c1e', border: '1px solid #2c2c2e', borderRadius: 12, padding: 20, marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Link to={`/profile/${r.author?.username}`} style={{ fontWeight: 600 }}>@{r.author?.username}</Link>
                          <StarRating value={r.rating} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#636366', fontSize: '0.8rem' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                          {!isOwnReview && rAuthorId && (
                            <ReportBlockMenu
                              targetType="review"
                              targetId={r.id}
                              targetUserId={rAuthorId}
                              targetLabel="review"
                              onReported={() => setReviews(prev => prev.filter(x => x.id !== r.id))}
                              onBlocked={(blockedUserId) => setReviews(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                              triggerSize="small"
                            />
                          )}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>{r.title}</div>
                      <div style={{ color: '#8e8e93', lineHeight: 1.6 }}>{r.body}</div>
                      {(r.pros || r.cons) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                          {r.pros && <div><div style={{ color: '#4caf7d', fontWeight: 600, fontSize: '0.8rem', marginBottom: 4 }}>PROS</div><div style={{ color: '#8e8e93', fontSize: '0.875rem' }}>{r.pros}</div></div>}
                          {r.cons && <div><div style={{ color: '#e84545', fontWeight: 600, fontSize: '0.8rem', marginBottom: 4 }}>CONS</div><div style={{ color: '#8e8e93', fontSize: '0.875rem' }}>{r.cons}</div></div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div onClick={() => setLightboxIdx(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => Math.max(0, i - 1)); }} disabled={lightboxIdx === 0}
            style={{ position: 'absolute', left: 24, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: '2rem', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          {isVideoItem(images[lightboxIdx])
            ? (
              <div onClick={e => e.stopPropagation()} style={{ width: '85vw', height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VideoMedia src={images[lightboxIdx]?.videoUrl} poster={images[lightboxIdx]?.imageUrl} className="video-media--contain" />
              </div>
            )
            : (
              <img src={images[lightboxIdx]?.imageUrl} alt="" onClick={e => e.stopPropagation()}
                style={{ maxWidth: '85vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 64px rgba(0,0,0,0.8)' }} />
            )}
          <button onClick={e => { e.stopPropagation(); setLightboxIdx(i => Math.min(images.length - 1, i + 1)); }} disabled={lightboxIdx === images.length - 1}
            style={{ position: 'absolute', right: 24, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: '2rem', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          <button onClick={() => setLightboxIdx(null)} style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: '1.8rem', cursor: 'pointer' }}>×</button>
          <div style={{ position: 'absolute', bottom: 20, color: '#8e8e93', fontSize: '0.875rem' }}>{lightboxIdx + 1} / {images.length}</div>
        </div>
      )}

      {/* Edit vehicle modal */}
      {showEdit && (
        <div className="event-modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="event-modal" style={{ maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="event-modal-header">
              <h2>Edit Vehicle</h2>
              <button className="event-modal-close" onClick={() => setShowEdit(false)}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} className="event-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group"><label>Nickname</label><input className="input" value={editForm.nickname} onChange={e => setEF('nickname', e.target.value)} placeholder="e.g. The Beast" /></div>
                <div className="form-group"><label>Colour</label><input className="input" value={editForm.color} onChange={e => setEF('color', e.target.value)} placeholder="e.g. Midnight Black" /></div>
                <div className="form-group"><label>Odometer (km)</label><input className="input" type="text" inputMode="numeric" pattern="[0-9]*" value={editForm.odometer} onChange={e => setEF('odometer', e.target.value.replace(/\D/g, ''))} placeholder="45000" /></div>
                <div className="form-group"><label>Condition</label>
                  <select className="input" value={editForm.condition} onChange={e => setEF('condition', e.target.value)}>
                    <option value="">— select —</option>
                    {[['excellent','Excellent'],['good','Good'],['fair','Fair'],['poor','Poor']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Transmission</label>
                  <select className="input" value={editForm.transmission} onChange={e => setEF('transmission', e.target.value)}>
                    <option value="">— select —</option>
                    {[['manual','Manual'],['automatic','Automatic'],['cvt','CVT'],['dct','DCT'],['other','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Drive Type</label>
                  <select className="input" value={editForm.driveType} onChange={e => setEF('driveType', e.target.value)}>
                    <option value="">— select —</option>
                    {[['fwd','FWD'],['rwd','RWD'],['awd','AWD'],['4wd','4WD'],['other','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Fuel Type</label>
                  <select className="input" value={editForm.fuelType} onChange={e => setEF('fuelType', e.target.value)}>
                    <option value="">— select —</option>
                    {[['petrol','Petrol'],['diesel','Diesel'],['electric','Electric'],['hybrid','Hybrid'],['other','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Horsepower (hp)</label><input className="input" type="number" value={editForm.horsepower} onChange={e => setEF('horsepower', e.target.value)} /></div>
                <div className="form-group"><label>Torque (Nm)</label><input className="input" type="number" value={editForm.torque} onChange={e => setEF('torque', e.target.value)} /></div>
                <div className="form-group"><label>Top Speed (km/h)</label><input className="input" type="number" value={editForm.topSpeed} onChange={e => setEF('topSpeed', e.target.value)} /></div>
                <div className="form-group"><label>Engine Displacement (L)</label><input className="input" type="number" step="0.1" value={editForm.engineDisplacement} onChange={e => setEF('engineDisplacement', e.target.value)} /></div>
                <div className="form-group"><label>Engine Cylinders</label><input className="input" type="number" value={editForm.engineCylinders} onChange={e => setEF('engineCylinders', e.target.value)} /></div>
                <div className="form-group"><label>0–100 km/h (s)</label><input className="input" type="number" step="0.1" value={editForm.zeroToHundred} onChange={e => setEF('zeroToHundred', e.target.value)} /></div>
              </div>
              <div className="form-group" style={{ marginTop: 4 }}>
                <label>Description</label>
                <textarea className="input" rows={3} value={editForm.description} onChange={e => setEF('description', e.target.value)} placeholder="Tell us about this vehicle..." />
              </div>
              <div className="form-group">
                <label>Notes (private)</label>
                <textarea className="input" rows={2} value={editForm.notes} onChange={e => setEF('notes', e.target.value)} placeholder="Private notes..." />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="submit" className="btn-primary" disabled={editSubmitting}>{editSubmitting ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit mod modal */}
      {showModModal && (
        <AddModModal
          mod={editingMod}
          onClose={() => { setShowModModal(false); setEditingMod(null); }}
          onSave={handleSaveMod}
          onDelete={editingMod ? async () => { await handleDeleteMod(editingMod.id); setShowModModal(false); setEditingMod(null); } : undefined}
        />
      )}

      {coverModalImg && (
        <ThumbnailCropModal
          imageUrl={coverModalImg.imageUrl}
          vehicleId={id}
          imageId={coverModalImg.id}
          onClose={() => setCoverModalImg(null)}
          onDone={handleCoverSet}
        />
      )}

      <audio ref={audioRef} />
    </>
  );
};

export default VehicleDetail;
